<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="520"
    content-class="quality-grading-modal"
    scrollable
    @click:outside="close"
  >
    <v-card>
      <v-toolbar dark color="success darken-2" flat>
        <v-toolbar-title class="font-weight-bold text-subtitle-1">
          {{ $t('memberDataSection.greenCoffee.greenBeansQualityGrading') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0 quality-scroll-body">
        <!-- Summary row -->
        <div class="d-flex justify-space-between px-6 pt-5 pb-2">
          <div>
            <div class="text-caption text--secondary">{{ $t('memberDataSection.greenCoffee.outturnNumber') }}</div>
            <div class="font-weight-bold text-subtitle-1 mt-1">{{ outturnNumber || '—' }}</div>
          </div>
          <div class="text-right">
            <div class="text-caption text--secondary">{{ $t('memberDataSection.greenCoffee.totalQuantity') }}</div>
            <div class="font-weight-bold text-subtitle-1 mt-1">{{ totalQuantityDisplay }}</div>
          </div>
        </div>

        <!-- Pie chart + legend -->
        <div v-if="pieSegments.length" class="d-flex align-center px-6 pt-2 pb-4">
          <svg width="180" height="180" viewBox="0 0 180 180" class="flex-shrink-0">
            <path
              v-for="seg in pieSegments"
              :key="'pie-' + seg.lotId"
              :d="seg.path"
              :fill="seg.color"
            />
          </svg>
          <div class="ml-6">
            <div
              v-for="seg in chartSegments"
              :key="'legend-' + seg.lotId"
              class="d-flex align-center mb-2"
            >
              <span
                class="legend-dot mr-2"
                :style="{ backgroundColor: seg.color }"
              ></span>
              <span class="text-body-2">{{ seg.lotId }}: {{ seg.percentage }}%</span>
            </div>
          </div>
        </div>

        <!-- Lot cards -->
        <div v-for="lot in localLots" :key="String(lot.id) + (lot.lotId || '')">
          <v-divider />
          <div class="px-6 py-5">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="font-weight-bold lot-id-label">Lot ID: {{ lot.lotId || '—' }}</span>
              <div class="d-flex align-center">
                <v-btn
                  fab
                  x-small
                  class="pencil-btn mr-2"
                  elevation="0"
                  @click="openAddQualityGrade(lot)"
                >
                  <v-icon small color="white">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  fab
                  x-small
                  class="delete-btn"
                  elevation="0"
                  @click="removeLot(lot)"
                >
                  <v-icon small color="white">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
            <v-row dense class="lot-detail-grid">
              <v-col cols="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.quantity') }}</div>
                <div class="field-value">{{ lot.quantity != null ? lot.quantity + ' kg' : '—' }}</div>
              </v-col>
              <v-col cols="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.packing') }}</div>
                <div class="field-value">{{ lot.unitSize != null ? lot.unitSize + ' kg Bag' : '—' }}</div>
              </v-col>
              <v-col cols="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalBags') }}</div>
                <div class="field-value">{{ lot.totalBags != null ? lot.totalBags : '—' }}</div>
              </v-col>
              <v-col cols="4" class="mt-3">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.leftoverQuantity') }}</div>
                <div class="field-value">{{ lot.leftoverQuantity != null ? lot.leftoverQuantity + ' kg' : '—' }}</div>
              </v-col>
              <v-col cols="4" class="mt-3">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.qualityScore') }}</div>
                <div class="field-value">{{ lot.qualityScore || '—' }}</div>
              </v-col>
              <v-col cols="4" class="mt-3">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.label') }}</div>
                <div class="field-value">{{ displayLabel(lot.label) }}</div>
              </v-col>
              <v-col v-if="lot.percentage != null" cols="12" class="mt-3">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentage') }}</div>
                <div class="field-value">{{ lot.percentage }}%</div>
              </v-col>
            </v-row>
          </div>
        </div>

        <div v-if="!localLots.length" class="px-6 py-6 text-center text--secondary text-body-2">
          {{ $t('memberDataSection.greenCoffee.noLotsYet') }}
        </div>

        <v-divider />

        <!-- Create Quality Grade button -->
        <div class="px-6 py-5">
          <v-btn
            outlined
            color="primary"
            class="text-none font-weight-bold"
            style="border-radius: 4px;"
            @click="openAddQualityGrade(null)"
          >
            <v-icon left small>mdi-ribbon</v-icon>
            {{ $t('memberDataSection.greenCoffee.createQualityGrade') }}
          </v-btn>
        </div>

        <v-divider />

        <!-- Finish Grading section -->
        <div class="px-6 py-5">
          <div class="font-weight-bold text-body-1 mb-2">{{ $t('memberDataSection.greenCoffee.finishGrading') }}</div>
          <div class="text-body-2 text--secondary">
            {{ $t('memberDataSection.greenCoffee.finishGradingDesc') }}
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" class="text-none px-6" @click="close">{{ $t('cancel') }}</v-btn>
        <v-btn color="primary" dark class="text-none px-6 ml-2" @click="finishGrading">
          {{ $t('memberDataSection.greenCoffee.finishGradingButton') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <AddQualityGradeModal
      v-model="addGradeModalVisible"
      :lot="selectedLot"
      :quality-scores="qualityScores"
      :labels="labels"
      :grade-options="gradeOptions"
      :processing-batch-code="processingBatchCode"
      @submit="onQualityGradeCreated"
    />
  </v-dialog>
</template>

<script>
import AddQualityGradeModal from "./AddQualityGradeModal.vue";

export default {
  name: "GreenBeansQualityGradingModal",
  components: { AddQualityGradeModal },
  props: {
    value: { type: Boolean, default: false },
    outturnNumber: { type: String, default: "" },
    totalQuantity: { type: [Number, String], default: 0 },
    unitSize: { type: [Number, String], default: 5 },
    lots: { type: Array, default: () => [] },
    qualityScores: { type: Array, default: () => [] },
    labels: { type: Array, default: () => [] },
    gradeOptions: { type: Array, default: () => [] },
    processingBatchCode: { type: String, default: "" },
  },
  data() {
    return {
      visible: this.value,
      addGradeModalVisible: false,
      selectedLot: null,
      localLots: [],
      chartColors: ["#4caf50", "#ff9800", "#9e9e9e"],
    };
  },
  computed: {
    totalQuantityDisplay() {
      const q = Number(this.totalQuantity) || 0;
      return q ? `${q} kg` : "—";
    },
    chartSegments() {
      return (this.localLots || [])
        .filter((l) => l.percentage != null)
        .map((l, i) => ({
          lotId: l.lotId,
          percentage: l.percentage,
          color: this.chartColors[i % this.chartColors.length],
        }));
    },
    pieSegments() {
      const cx = 90, cy = 90, r = 82;
      const segments = this.chartSegments.slice();
      const total = segments.reduce((s, l) => s + l.percentage, 0);
      if (total < 100) {
        segments.push({ lotId: "__rest__", percentage: 100 - total, color: "#bdbdbd" });
      }
      const paths = [];
      let currentAngle = -90;
      for (const seg of segments) {
        if (seg.percentage <= 0) continue;
        const sweep = (seg.percentage / 100) * 360;
        const path = this.describeSlice(cx, cy, r, currentAngle, currentAngle + sweep);
        paths.push({ ...seg, path });
        currentAngle += sweep;
      }
      return paths;
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) this.localLots = [...(this.lots || [])];
    },
    visible(v) {
      this.$emit("input", v);
    },
    lots: {
      handler(val) {
        this.localLots = [...(val || [])];
      },
      immediate: true,
    },
  },
  methods: {
    displayLabel(label) {
      if (label == null || label === "" || label === "—") return "—";
      if (typeof label === "object") {
        const title = label.title || label.name;
        if (title) return title;
        return label.id != null ? String(label.id) : "—";
      }
      if (typeof label === "string") {
        const trimmed = label.trim();
        if (!trimmed || trimmed === "{}") return "—";
        if (trimmed.startsWith("{")) {
          try {
            const p = JSON.parse(trimmed);
            const title = p?.title || p?.name;
            if (title) return title;
            if (p?.id != null) return String(p.id);
            return "—";
          } catch (e) {
            return trimmed === "{}" ? "—" : trimmed;
          }
        }
        return trimmed;
      }
      return "—";
    },
    describeSlice(cx, cy, r, startDeg, endDeg) {
      if (endDeg - startDeg >= 359.999) {
        const topX = cx;
        const topY = cy - r;
        const bottomX = cx;
        const bottomY = cy + r;
        return `M ${cx} ${cy} L ${topX.toFixed(2)} ${topY.toFixed(
          2
        )} A ${r} ${r} 0 1 1 ${bottomX.toFixed(2)} ${bottomY.toFixed(
          2
        )} A ${r} ${r} 0 1 1 ${topX.toFixed(2)} ${topY.toFixed(2)} Z`;
      }
      const toRad = (deg) => (deg * Math.PI) / 180;
      const x1 = cx + r * Math.cos(toRad(startDeg));
      const y1 = cy + r * Math.sin(toRad(startDeg));
      const x2 = cx + r * Math.cos(toRad(endDeg));
      const y2 = cy + r * Math.sin(toRad(endDeg));
      const large = endDeg - startDeg > 180 ? 1 : 0;
      return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
    },
    close() {
      this.visible = false;
      this.$emit("close");
    },
    openAddQualityGrade(lot) {
      this.selectedLot = lot || null;
      this.addGradeModalVisible = true;
    },
    removeLot(lot) {
      this.localLots = this.localLots.filter(
        (l) => !(l.id === lot.id && l.lotId === lot.lotId)
      );
      this.recalculatePercentages();
    },
    onQualityGradeCreated(grade) {
      this.addGradeModalVisible = false;

      const newQty = Number(grade.quantity) || 0;
      const totalBase = Number(this.totalQuantity) || 0;

      // Compute tentative total if this grade is applied (respecting edit vs new)
      let currentTotal = this.localLots.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
      if (this.selectedLot) {
        const existingIdx = this.localLots.findIndex(
          (l) => l.id === this.selectedLot.id && l.lotId === this.selectedLot.lotId
        );
        if (existingIdx >= 0) {
          const existingQty = Number(this.localLots[existingIdx].quantity) || 0;
          currentTotal -= existingQty;
        }
      }
      const tentativeTotal = currentTotal + newQty;

      if (totalBase > 0 && tentativeTotal > totalBase) {
        // Do not apply change if it exceeds total green beans
        if (this.$notify) {
          this.$notify({
            title: this.$t
              ? this.$t("memberDataSection.greenCoffee.totalGreenBeansExceeded")
              : "Total graded quantity cannot exceed Total Green Beans",
            type: "error",
          });
        }
        // Keep modal closed but do not alter lots
        this.selectedLot = null;
        return;
      }

      if (this.selectedLot) {
        // Update existing lot (pencil / edit flow)
        const idx = this.localLots.findIndex(
          (l) => l.id === this.selectedLot.id && l.lotId === this.selectedLot.lotId
        );
        if (idx >= 0) {
          this.$set(this.localLots, idx, {
            ...this.localLots[idx],
            quantity: grade.quantity,
            unitSize: grade.unitSize,
            totalBags: grade.totalBags,
            leftoverQuantity: grade.leftoverQuantity,
            qualityScore: grade.qualityScore,
            label: grade.label,
          });
        } else {
          this.localLots.push(this.buildLot(grade));
        }
      } else {
        // Add new lot (CREATE QUALITY GRADE flow)
        this.localLots.push(this.buildLot(grade));
      }

      this.recalculatePercentages();
      this.selectedLot = null;
      this.$emit("add-grade", grade);
    },
    buildLot(grade) {
      return {
        id: (grade.lotId || "") + "_" + Date.now(),
        lotId: grade.lotId,
        quantity: grade.quantity,
        unitSize: grade.unitSize,
        totalBags: grade.totalBags,
        leftoverQuantity: grade.leftoverQuantity,
        qualityTitle: grade.qualityTitle || "",
        qualityScore: grade.qualityScore || "",
        label: grade.label || "",
        percentage: null,
      };
    },
    recalculatePercentages() {
      const lotsTotal = this.localLots.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
      const baseTotal = Number(this.totalQuantity) || lotsTotal;
      if (baseTotal <= 0) return;
      this.localLots = this.localLots.map((l) => ({
        ...l,
        percentage: Math.round(((Number(l.quantity) || 0) / baseTotal) * 100),
      }));
    },
    finishGrading() {
      this.$emit("finish-grading", [...this.localLots]);
      this.close();
    },
  },
};
</script>

<style scoped>
.quality-scroll-body {
  overflow-y: auto;
  max-height: calc(90vh - 120px);
}
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.lot-id-label {
  color: #6d4c41;
  font-size: 1rem;
}
.pencil-btn {
  background-color: #ff9800 !important;
  width: 32px !important;
  height: 32px !important;
}
.delete-btn {
  background-color: #e53935 !important;
  width: 32px !important;
  height: 32px !important;
}
.field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.4;
}
.field-value {
  font-weight: 700;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 2px;
}
</style>
